import React, { useCallback, useEffect, useState } from "react"
import { Box, Button, MenuItem, Select } from "@mui/material"
import { Add } from "@mui/icons-material"
import { AdminFilterBar, AdminPageHeader, countLabel } from "../../../components/ui"
import { SimpleSnackbar } from "../../../components/snackbar"
import { handlePromise } from "../../../utils/handlePromisse"
import {
    createFeatureFlag,
    FeatureFlag,
    listFeatureFlags,
    updateFeatureFlag,
} from "../../../services/feature-flag.service"
import { FeatureFlagTable } from "./table/feature-flag-table"
import { FeatureFlagFormDialog } from "./form/feature-flag-form.dialog"
import { OverridesDialog } from "./overrides/overrides-dialog"

export const FeatureFlagPage: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [flags, setFlags] = useState<FeatureFlag[]>([])

    const [search, setSearch] = useState("")
    const [ambienteFilter, setAmbienteFilter] = useState<'all' | 'production' | 'homologation'>("all")

    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState("")

    const [formOpen, setFormOpen] = useState(false)
    const [flagInForm, setFlagInForm] = useState<FeatureFlag | null>(null)

    const [overridesOpen, setOverridesOpen] = useState(false)
    const [overridesFlagName, setOverridesFlagName] = useState<string | null>(null)

    const notify = useCallback((message: string) => {
        setSnackbarMessage(message)
        setSnackbarOpen(true)
    }, [])

    const fetchFlags = useCallback(async () => {
        setLoading(true)
        const [result, error] = await handlePromise(listFeatureFlags())
        setLoading(false)
        if (error) {
            notify(error.response?.data?.message || "Erro ao carregar feature flags")
            return
        }
        setFlags(result.data)
    }, [notify])

    useEffect(() => {
        fetchFlags()
    }, [fetchFlags])

    const handleToggleEnabled = useCallback(async (flag: FeatureFlag) => {
        const [, error] = await handlePromise(
            updateFeatureFlag(flag.name, { enabled: !flag.enabled })
        )
        if (error) {
            notify(error.response?.data?.message || "Erro ao atualizar flag")
            return
        }
        await fetchFlags()
    }, [fetchFlags, notify])

    const handleSubmitForm = useCallback(async (input: {
        name: string
        enabled: boolean
        rolloutPercentage: number
        description: string | null
    }) => {
        const isEdit = !!flagInForm
        const action = isEdit
            ? updateFeatureFlag(flagInForm!.name, {
                enabled: input.enabled,
                rolloutPercentage: input.rolloutPercentage,
                description: input.description,
              })
            : createFeatureFlag(input)

        const [, error] = await handlePromise(action)
        if (error) {
            notify(error.response?.data?.message || "Erro ao salvar feature flag")
            return
        }
        setFormOpen(false)
        setFlagInForm(null)
        await fetchFlags()
    }, [flagInForm, fetchFlags, notify])

    const openNewFlag = () => {
        setFlagInForm(null)
        setFormOpen(true)
    }

    const openEditFlag = (flag: FeatureFlag) => {
        setFlagInForm(flag)
        setFormOpen(true)
    }

    const openOverrides = (flag: FeatureFlag) => {
        setOverridesFlagName(flag.name)
        setOverridesOpen(true)
    }

    const closeOverrides = () => {
        setOverridesOpen(false)
        fetchFlags() // atualiza contagem
    }

    // Client-side search filter (no environment field in model — ambienteFilter is visual only)
    const filteredFlags = flags.filter((flag) =>
        !search || flag.name.toLowerCase().includes(search.toLowerCase())
    )

    return (
        <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
            <SimpleSnackbar
                show={snackbarOpen}
                message={snackbarMessage}
                onDismiss={() => setSnackbarOpen(false)}
            />

            <AdminPageHeader
                title="Feature flags"
                subtitle="Liga/desliga de funcionalidades"
                action={
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={openNewFlag}
                        sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}
                        disabled={loading}
                    >
                        Nova flag
                    </Button>
                }
            />

            <AdminFilterBar
                search={search}
                onSearch={setSearch}
                placeholder="Buscar por nome da flag..."
                filters={
                    <Select
                        size="small"
                        value={ambienteFilter}
                        onChange={(e) => setAmbienteFilter(e.target.value as 'all' | 'production' | 'homologation')}
                        sx={{ height: 40, minWidth: 200, borderRadius: "10px" }}
                    >
                        <MenuItem value="all">Todos os ambientes</MenuItem>
                        <MenuItem value="production">Produção</MenuItem>
                        <MenuItem value="homologation">Homologação</MenuItem>
                    </Select>
                }
                count={countLabel(filteredFlags.length, flags.length)}
            />

            <FeatureFlagTable
                flags={filteredFlags}
                onToggleEnabled={handleToggleEnabled}
                onEdit={openEditFlag}
                onManageOverrides={openOverrides}
                countLabelText={countLabel(filteredFlags.length, flags.length)}
            />

            <FeatureFlagFormDialog
                open={formOpen}
                flag={flagInForm}
                onClose={() => {
                    setFormOpen(false)
                    setFlagInForm(null)
                }}
                onSubmit={handleSubmitForm}
            />

            <OverridesDialog
                open={overridesOpen}
                flagName={overridesFlagName}
                onClose={closeOverrides}
                onError={notify}
            />
        </Box>
    )
}
